import { Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, OnChanges, OnInit, SimpleChanges, untracked, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { GlobalSearchResult } from '../../services/global-search.service';
import { TicketReportFlowService } from '../../ticketing/services/ticket-report-flow.service';
import { TicketType } from '../../ticketing/enums/ticketing.enum';
import { OrgIdentity } from '../../shared/org-identity/org-identity';
import { NgxPermissionsService } from 'ngx-permissions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter } from 'rxjs/operators';
import { CorporateFormsService } from '../../corporate/services/corporate-forms.service';
import { PermissionsService } from '../../services/permissions.service';
import { COMPANY_SETTINGS_SECTIONS } from '../../shared/navigation/company-settings-sections';
import { HeaderComponent } from '../header/header.component';
import { PortalApplicationsService } from '../../corporate/portal-applications/portal-applications.service';
import { ModulesService } from '../../services/modules.service';
import { IModule } from '../../models/module/IModule';
import { BcApp } from '../../shared/ui/app-switcher/bc-app-switcher.component';
import { FinderItem, PageFinderService } from '../../shared/page-finder/page-finder.service';
import { BcLayoutService } from '../../core/ui/bc-layout.service';
import { BcTab } from '../../shared/ui/tab-bar/bc-tab-bar.component';
import { OrgFaviconService, OrgIdentityService, OrgLensService } from '../../shared/org-identity';
import { WorkspacePreferencesService } from '../../shared/org-identity/workspace-preferences.service';

/** SPEC.md › App switcher grouping (a proposal until product confirms it). */
const APP_GROUPS: Record<number, string> = {
  1: 'Workspace',
  2: 'Finance & tax', 7: 'Finance & tax', 12: 'Finance & tax', 6: 'Finance & tax', 8: 'Finance & tax', 10: 'Finance & tax',
  3: 'Compliance & audit', 9: 'Compliance & audit', 15: 'Compliance & audit', 14: 'Compliance & audit',
  4: 'People & operations', 5: 'People & operations', 13: 'People & operations', 11: 'People & operations',
};
const GROUP_ORDER = ['Workspace', 'Finance & tax', 'Compliance & audit', 'People & operations', 'Other'];
const COLLAPSED_KEY = 'bc.sidebarCollapsed';

/** badge: which count to show beside the link ('portalNeedsAction' = New from the portal › Needs action). */
interface NavLink { name: string; icon?: string; route: string; queryParam?: any; queryParams?: Record<string, any> | null; children?: any[] | null; notesOnly?: boolean; badge?: 'portalNeedsAction'; }

/**
 * One entry of the One Workspace sidebar: a single list for the whole app, no module switching.
 * Opening an entry of another module does the module bookkeeping in place (applyModuleInPlace) and
 * navigates in the same tab.
 */
interface WorkspaceItem {
  key: string;
  label: string;
  icon: string;
  /** The module the page belongs to; null for pages outside any module (Inbox, Approvals, Settings). */
  moduleId: number | null;
  commands: any[];
  queryParams?: Record<string, any> | null;
  /** Path prefixes (no leading slash, segment-aware) that make the entry the active one. */
  paths: string[];
  /** The module's home dashboard ('/' with ?module=) is this entry's page. */
  homeModule?: number;
  /**
   * Which of the module's existing menu entries (by route) show under the entry while it is active:
   * a list, or 'rest' for every entry no other item of the module claims. Unset: none.
   */
  menuRoutes?: string[] | 'rest';
  /** A separate app (Billing): a plain link that opens in a new tab. */
  externalUrl?: string;
  /** Only counts the shell really has. */
  badge?: 'notesUnread';
  /** Extra gate on top of module access. */
  gate?: () => boolean;
  /** Filled in by rebuildWorkspace. */
  href?: string;
}

interface WorkspaceSection { key: string; heading: string | null; items: WorkspaceItem[]; }

/**
 * Option D app shell (docs/redesign, SPEC.md › Layout) with the One Workspace sidebar: one list for
 * the whole app (no app switcher), the org switcher, Create, the active entry's module pages under it
 * and the account menu;
 * light top bar with breadcrumb, Ctrl K search, contacts, notes, messages and notifications.
 * Rendered by AppComponent on every route and screen size; phones and tablets get their own layouts
 * (docs/redesign/mobile, see `layout` below).
 *
 * It EXTENDS HeaderComponent rather than re-implementing it. The header owns the menu, the
 * global search and its scope filters, the company picker, notifications, module switching and
 * the account entries — about 1,800 lines of behaviour the redesign must not change. The
 * subclass keeps all of it and only swaps the template, plus the bits of state the new layout
 * needs (expanded sections, the collapsed rail, the app list for the switcher, the breadcrumb).
 */
/** Module ids whose workspace already has its own section menu inside the page. */
const OWN_SECTION_MENU: ReadonlySet<number> = new Set([2, 4, 5, 6, 7, 9, 11, 13, 15]);

@Component({
  selector: 'bc-app-shell',
  templateUrl: './bc-app-shell.component.html',
  styleUrls: ['./bc-app-shell.component.scss'],
  standalone: false,
})
export class BcAppShellComponent extends HeaderComponent implements OnInit, OnChanges {
  private readonly nav = inject(Router);
  private readonly apps$ = inject(ModulesService);

  /** Icon-rail sidebar. A per-viewer preference, so it lives in localStorage. */
  collapsed = readCollapsed();
  /** Parent items (by name) whose children are showing. */
  expanded = new Set<string>();
  /** The current app's pages, with View/Create sub-levels folded (see foldChildren). */
  navItems: NavLink[] = [];
  /** Every "create-form" entry in the menu, for the Create button. */
  createItems: NavLink[] = [];
  apps: BcApp[] = [];
  /** Every module's name by id, for the Welcome panel's Modules column. */
  private moduleNames: Record<number, string> = {};
  /** Notes is hidden for Corporate Viewers and notes-restricted tenants (canSeeGeneralNotes). */
  canSeeNotesNav = false;
  notesUnread = 0;
  /** Portal clients waiting on staff (Records › New from the portal). Refreshed by that page too. */
  private readonly portalApplications = inject(PortalApplicationsService);
  readonly portalNeedsAction = this.portalApplications.needsActionCount;
  /** The top bar's "What are you looking for?" button opens it; the card itself is in AppComponent. */
  readonly pageFinder = inject(PageFinderService);

  /** Organisation badges, the top bar's colour stripe and the tab icon (docs/multi-org). */
  readonly orgIdentity = inject(OrgIdentityService);
  private readonly orgLens = inject(OrgLensService);
  private readonly orgFavicon = inject(OrgFaviconService);
  /** Global search covers the organisations the top bar's filter shows. */
  private readonly searchFollowsLens = effect(() => {
    this.selectedSearchCompanyIds = this.orgLens.isAll() ? [] : [...this.orgLens.selectedIds()];
  });

  badgeFor(link: NavLink): number {
    return link.badge === 'portalNeedsAction' ? this.portalNeedsAction() : 0;
  }

  // ---- One Workspace sidebar ----

  /** The sidebar's sections, holding only the entries this user can open. */
  workspaceSections: WorkspaceSection[] = [];
  /** The entry the URL is on (router events, not the last click). */
  activeKey: string | null = null;
  /** The active entry's pages: its module's existing menu entries (see WorkspaceItem.menuRoutes). */
  subMenu: NavLink[] = [];
  /** Module access, resolved in buildApps with the same check the module switcher uses. */
  private appAccess: Record<number, boolean> = {};
  private accessKnown = false;

  badgeForItem(item: WorkspaceItem): number {
    return item.badge === 'notesUnread' ? this.notesUnread : 0;
  }

  trackItem(_: number, item: WorkspaceItem): string { return item.key; }
  trackSection(_: number, section: WorkspaceSection): string { return section.key; }

  /** A sub-menu link: a module's own top-level page uses the menu's longest-match, a sub-page the exact match. */
  isSubLinkActive(link: NavLink): boolean {
    return this.navItems.includes(link) ? this.isNavItemActive(link) : this.isChildActive(link);
  }

  /**
   * Same tab, no reload. Ctrl/⌘/Shift/middle-click fall through to the real link (new tab / window);
   * the external Billing app is a target=_blank link anyway.
   */
  openItem(item: WorkspaceItem, event?: MouseEvent): void {
    if (item.externalUrl) return;
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) return;
    event?.preventDefault();
    if (item.moduleId != null && item.moduleId !== this.currentAppId) this.applyModuleInPlace(item.moduleId);
    void this.nav.navigate(item.commands, item.queryParams ? { queryParams: item.queryParams } : {});
  }

  /**
   * The design's list (My work · Inbox · Notes · Planner / Clients / Finance / People / Insights /
   * Settings), plus every other module under More, so no page is lost.
   */
  private workspaceDefinition(): { key: string; heading: string | null; items: WorkspaceItem[] }[] {
    const landing = (id: number): Pick<WorkspaceItem, 'commands' | 'queryParams' | 'homeModule'> => {
      const route = this.moduleLandingRoute(id);
      return route
        ? { commands: route, queryParams: { module: id } }
        : { commands: [''], queryParams: { module: id }, homeModule: id };
    };
    const billing = this.apps.find(a => a.id === 12 && a.externalUrl);
    return [
      { key: 'top', heading: null, items: [
        { key: 'my-work', label: 'My work', icon: 'home', moduleId: 1, ...landing(1), paths: [], menuRoutes: [''] },
        { key: 'inbox', label: 'Inbox', icon: 'inbox', moduleId: null, commands: ['/notifications'], paths: ['notifications', 'global-requests'] },
        { key: 'notes', label: 'Notes', icon: 'forum', moduleId: 1, commands: ['/tagged-notes'], paths: ['tagged-notes'],
          menuRoutes: ['tagged-notes'], badge: 'notesUnread', gate: () => this.canSeeNotesNav },
        { key: 'planner', label: 'Planner', icon: 'view_kanban', moduleId: 1, commands: ['/task-planner'], paths: ['task-planner'],
          menuRoutes: ['task-planner'] },
      ] },
      { key: 'clients', heading: 'Clients', items: [
        { key: 'clients', label: 'Clients', icon: 'group', moduleId: 1, commands: ['/forms-table'], queryParams: { category: 'clients' },
          paths: ['forms-table', 'edit-form', 'create-form', 'portal-applications'], menuRoutes: ['forms-table'] },
        { key: 'documents', label: 'Documents', icon: 'folder_open', moduleId: 1, commands: ['/file-system'], paths: ['file-system'],
          menuRoutes: ['file-system'] },
      ] },
      { key: 'finance', heading: 'Finance', items: [
        { key: 'invoicing', label: 'Invoicing', icon: 'receipt_long', moduleId: 2, ...landing(2),
          paths: ['invoicing', 'invoicing-dashboard'], menuRoutes: 'rest' },
        { key: 'accounting', label: 'Accounting', icon: 'account_balance', moduleId: 7, commands: ['/accounting'],
          paths: ['accounting'], menuRoutes: 'rest' },
        { key: 'timesheets', label: 'Timesheets', icon: 'schedule', moduleId: 1, commands: ['/time-sheet'], paths: ['time-sheet'],
          menuRoutes: ['time-sheet'] },
      ] },
      { key: 'people', heading: 'People', items: [
        { key: 'hr', label: 'HR and leave', icon: 'badge', moduleId: 4, ...landing(4), paths: ['hr', 'employee'], menuRoutes: 'rest' },
        // Pending changes is admin-only (the account menu and pagePermissionGuard agree).
        { key: 'approvals', label: 'Approvals', icon: 'approval', moduleId: null, commands: ['/admin/pending-changes'],
          paths: ['admin/pending-changes'], gate: () => this.corporateAccess.isCorporateAdmin() },
      ] },
      // The boards close the list with Reports and Settings under Insights.
      { key: 'insights', heading: 'Insights', items: [
        { key: 'reports', label: 'Reports', icon: 'monitoring', moduleId: 5, ...landing(5),
          paths: ['reports', 'profitability-report', 'time-sheet-reports'], menuRoutes: 'rest' },
        { key: 'settings', label: 'Settings', icon: 'settings', moduleId: null, commands: ['/settings/user'], paths: ['settings/user'] },
      ] },
      { key: 'more', heading: 'More', items: [
        { key: 'compliance', label: 'Compliance', icon: 'verified_user', moduleId: 3, ...landing(3),
          paths: ['corporate/risk-settings'], menuRoutes: 'rest' },
        { key: 'tax', label: 'Tax', icon: 'percent', moduleId: 6, ...landing(6), paths: ['tax'], menuRoutes: 'rest' },
        { key: 'inventory', label: 'Inventory', icon: 'inventory_2', moduleId: 13, ...landing(13), paths: ['inventory'], menuRoutes: 'rest' },
        { key: 'migration', label: 'Migration', icon: 'flight', moduleId: 11, ...landing(11), paths: ['migration'], menuRoutes: 'rest' },
        { key: 'audit', label: 'Audit', icon: 'policy', moduleId: 9, ...landing(9), paths: ['audit'], menuRoutes: 'rest' },
        { key: 'custodis', label: 'Custodis', icon: 'lock', moduleId: 15, ...landing(15), paths: ['custodis'], menuRoutes: 'rest' },
        { key: 'signatures', label: 'Signatures', icon: 'draw', moduleId: 14, ...landing(14), paths: ['signatures'], menuRoutes: 'rest' },
        { key: 'reference-numbers', label: 'Reference numbers', icon: 'tag', moduleId: 1, commands: ['/reference-numbers'],
          paths: ['reference-numbers'], menuRoutes: ['reference-numbers'] },
        ...(billing ? [{ key: 'billing', label: billing.label, icon: 'credit_card', moduleId: 12, commands: [],
          paths: [], externalUrl: billing.externalUrl } as WorkspaceItem] : []),
      ] },
    ];
  }

  /** Re-filters the list by access and rebuilds each link (its ?org= and ?module= follow the tab). */
  private rebuildWorkspace(): void {
    const org = this.companiesService.managedCompanyId;
    this.workspaceSections = this.workspaceDefinition()
      .map(section => ({
        ...section,
        items: section.items
          .filter(item => item.moduleId == null || !!this.appAccess[item.moduleId])
          .filter(item => !item.gate || item.gate())
          .map(item => ({ ...item, href: item.externalUrl || this.itemHref(item, org) })),
      }))
      .filter(section => section.items.length > 0);
    this.refreshActive();
  }

  /**
   * A real link for right-click / middle-click: it carries ?module= and this tab's ?org= so a new tab
   * opens on the same module and organisation. Notes links stay bare (see LinkContextMenuService.withOrg).
   */
  private itemHref(item: WorkspaceItem, org: number | null | undefined): string {
    const params: Record<string, any> = { ...(item.queryParams || {}) };
    const isNotes = item.commands[0] === '/tagged-notes';
    if (item.moduleId != null && !isNotes) params['module'] = item.moduleId;
    if (org && !isNotes) params['org'] = org;
    const route = item.commands.map(c => String(c)).join('/').replace(/^\/+/, '').replace(/\/{2,}/g, '/');
    return this.buildNavHref({ route, queryParams: Object.keys(params).length ? params : null });
  }

  private get workspaceItems(): WorkspaceItem[] {
    return this.workspaceSections.flatMap(s => s.items);
  }

  /** Picks the active entry from the URL, keeps the tab's module in step with it and fills the sub-menu. */
  private refreshActive(allowSwitch = true): void {
    const items = this.workspaceItems;
    const active = this.findActiveItem(items);
    this.activeKey = active?.key ?? null;

    // Reached by URL (back button, a link inside a page, the top bar): switch the module in place so
    // the sub-menu and the module's read-only flags match the page. Only into modules the user has.
    // Not from ngOnChanges: the switch re-renders AppComponent synchronously (its changeMenu).
    if (allowSwitch && active?.moduleId != null && this.accessKnown && this.currentAppId != null
        && active.moduleId !== this.currentAppId && this.appAccess[active.moduleId] && !active.externalUrl) {
      this.applyModuleInPlace(active.moduleId); // its menu lands in ngOnChanges, which calls back here
      return;
    }
    // Modules that draw their own section menu (Invoicing, HR, Reports, Tax, Accounting, Audit, Migration,
    // Inventory, Custodis) don't repeat it here: one menu per page, not two.
    this.subMenu = active && active.moduleId === this.currentAppId && !OWN_SECTION_MENU.has(active.moduleId)
      ? this.subMenuFor(active, items) : [];
  }

  private findActiveItem(items: WorkspaceItem[]): WorkspaceItem | null {
    const path = this.currentPath;
    if (path === '/') {
      const module = +(this.currentQuery['module'] || this.currentAppId || 1);
      return items.find(i => i.homeModule === module) ?? null;
    }
    const matches = (route: string) => {
      const r = '/' + route.replace(/^\/+/, '');
      return r !== '/' && (path === r || path.startsWith(r + '/'));
    };
    let best: WorkspaceItem | null = null;
    let bestLength = -1;
    for (const item of items) {
      for (const p of item.paths) {
        if (matches(p) && p.length > bestLength) { best = item; bestLength = p.length; }
      }
    }
    if (best) return best;
    // Otherwise a page of the current module's menu belongs to the entry that shows that menu entry.
    const routesOf = (node: any): string[] => [node?.route, ...(node?.children || []).flatMap(routesOf)].filter((r: any) => !!r);
    const entry = this.navItems.find(n => routesOf(n).some(matches));
    return entry ? items.find(i => i.moduleId === this.currentAppId && this.ownedEntries(i, items).includes(entry)) ?? null : null;
  }

  /** The current module's menu entries an item shows (see WorkspaceItem.menuRoutes). */
  private ownedEntries(item: WorkspaceItem, items: WorkspaceItem[]): NavLink[] {
    if (!item.menuRoutes) return [];
    if (item.menuRoutes !== 'rest') {
      const routes = item.menuRoutes;
      return this.navItems.filter(n => routes.includes(n.route));
    }
    const claimed = items
      .filter(i => i !== item && i.moduleId === item.moduleId && Array.isArray(i.menuRoutes))
      .flatMap(i => i.menuRoutes as string[]);
    return this.navItems.filter(n => !claimed.includes(n.route));
  }

  /** One owned entry shows its own pages; several show as a list, entries with pages as groups. */
  private subMenuFor(item: WorkspaceItem, items: WorkspaceItem[]): NavLink[] {
    const entries = this.ownedEntries(item, items).filter(n => !n.notesOnly || this.canSeeNotesNav);
    if (entries.length === 1) return entries[0].children ?? [];
    return entries;
  }

  /** Sidebar items the user may see. */
  get visibleNavItems(): NavLink[] {
    return this.navItems.filter(i => !i.notesOnly || this.canSeeNotesNav);
  }
  currentAppId: number | null = null;
  breadcrumb: string[] = [];

  private appsRun = 0;
  private currentPath = '';
  private currentQuery: Record<string, string> = {};

  /** Record pages (client records, organisation-company settings) use the icon rail (SPEC.md). */
  private onRecordPage = false;
  /** Expanded by hand on the current record page. Not saved: the next record opens as a rail. */
  private expandedOnRecord = false;
  /**
   * Notes opened in a new tab (a note's "Open in new tab", a shared note link, the top bar's Notes
   * button) is used like a chat window, so it gets the whole width: no sidebar, only the top bar.
   * Reaching Notes from the sidebar's Notes item stays an ordinary page with the sidebar. The tab
   * counts as opened for Notes when its first load was /tagged-notes; leaving Notes in that tab
   * brings the sidebar back.
   */
  isChatPage = false;
  private readonly openedForNotes = window.location.pathname === '/tagged-notes';

  /**
   * Phone / tablet / desktop (docs/redesign/mobile). Desktop (>= 1280) keeps the behaviour above.
   * Tablets always show the 80px rail with labels; opening it (a section, the chevron) lays the full
   * sidebar over the page. Phones have no sidebar: an app bar, bottom tabs and the full sidebar
   * as a drawer.
   */
  readonly layout = inject(BcLayoutService);

  /** The full sidebar is laid over the page (phone drawer, tablet rail opened). */
  get overlayNav(): boolean {
    return (this.layout.isPhone() || this.layout.useRail()) && this.isMobileMenuOpen;
  }

  get isRail(): boolean {
    if (this.layout.isPhone()) return false;
    if (this.layout.useRail()) return !this.isMobileMenuOpen;
    return this.onRecordPage ? !this.expandedOnRecord : this.collapsed;
  }

  /** Tablet rail: icons carry a short label underneath. The desktop's collapsed rail stays icon-only. */
  get railLabels(): boolean { return this.isRail && this.layout.useRail(); }

  /** Phone app bar title: the page, else the app. */
  get pageTitle(): string {
    return this.breadcrumb[this.breadcrumb.length - 1] || 'Bizcombo';
  }

  /** Phone bottom tabs (mockups/phone): rebuilt when the URL, the app or a count changes. */
  phoneTabs: BcTab[] = [];
  get moreTabActive(): boolean { return !this.phoneTabs.some(t => t.active); }

  get showTabBar(): boolean {
    return this.layout.isPhone() && !this.layout.immersive() && this.loginDisplay && !this.isOnboarding && !this.isChatPage;
  }

  /** A page holds the whole phone screen (BcLayoutService.immersive): no app bar either. */
  get phoneImmersive(): boolean { return this.layout.isPhone() && this.layout.immersive(); }

  get userInitials(): string {
    const u = this.currentUser as any;
    const fromName = [u?.firstName, u?.lastName].filter(Boolean).map((s: string) => s[0]).join('');
    return (fromName || u?.email?.substring(0, 2) || 'U').toUpperCase();
  }

  get userDisplayName(): string {
    const u = this.currentUser as any;
    return [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.email || '';
  }

  override ngOnInit(): void {
    // Subscribe before the header's init emits the first module.
    this.changeModule.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      this.currentAppId = id;
      this.updateBreadcrumb();
    });
    super.ngOnInit();

    this.canSeeNotes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.canSeeNotesNav = !!v;
      this.rebuildWorkspace();
      this.refreshTabs();
    });
    this.meetingNotesPanelService.unreadCount$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((n: any) => {
        this.notesUnread = Number(n) || 0;
        this.refreshTabs();
      });

    // The sidebar's "New from the portal" count: only while Corporate (whose menu has the link) is
    // open, re-read when the organisation changes.
    combineLatest([this.changeModule, this.companiesService.getManagedCompanyIdObsValue()])
      .pipe(
        filter(([moduleId, companyId]) => moduleId === 1 && !!companyId),
        distinctUntilChanged(([, a], [, b]) => a === b),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([, companyId]) => this.portalApplications.refreshCounts(companyId));

    // The org id is part of each app's new-tab link, so rebuild once this tab knows its org.
    combineLatest([this.apps$.getModules(), this.permissionsChanged$, this.companiesService.getManagedCompanyIdObsValue()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([modules]) => {
        this.moduleNames = Object.fromEntries(modules.map(m => [m.id, m.name]));
        void this.buildApps(modules);
      });

    this.paletteControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.paletteOpen) this.onPaletteInput();
    });

    this.pageFinder.registerSource(this, () => this.finderItems());
    this.destroyRef.onDestroy(() => this.pageFinder.unregisterSource(this));

    this.readUrl(this.nav.url);
    this.nav.events.pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((e: NavigationEnd) => {
        this.readUrl(e.urlAfterRedirects);
        this.isMobileMenuOpen = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menu']) {
      this.navItems = (this.menu || []).map(item => ({ ...item, children: this.foldChildren(item.children) }));
      this.createItems = collectCreateItems(this.menu || []);
      this.expandActive();
      this.refreshActive(false);
      this.updateBreadcrumb();
    }
  }

  // ---- Sidebar ----

  toggleRail(): void {
    // Tablets: the rail is fixed; "expanding" lays the full sidebar over the page instead.
    if (this.layout.useRail()) {
      this.isMobileMenuOpen ? this.closeMobileNav() : this.openMobileNav();
      return;
    }
    if (this.onRecordPage) {
      this.expandedOnRecord = !this.expandedOnRecord;
      return;
    }
    this.collapsed = !this.collapsed;
    try { localStorage.setItem(COLLAPSED_KEY, this.collapsed ? '1' : '0'); } catch { /* per-viewer nicety only */ }
  }

  isExpanded(item: NavLink): boolean { return this.expanded.has(item.name); }

  toggleSection(item: NavLink): void {
    if (this.isRail) {
      // The rail has no room for children: open the sidebar on the section instead.
      this.toggleRail();
      this.expanded.add(item.name);
      return;
    }
    this.expanded.has(item.name) ? this.expanded.delete(item.name) : this.expanded.add(item.name);
  }

  /** A child is active when its route and every query param it carries match the URL. */
  isChildActive(child: NavLink): boolean {
    if (this.currentPath !== '/' + child.route.replace(/^\/+/, '')) return false;
    const params = { ...(child.queryParams || {}) };
    if (child.queryParam !== null && child.queryParam !== undefined) params['type'] = child.queryParam;
    // A page opened without ?tab= shows its first sub-page, so the first child owns it.
    if ('tab' in params && this.currentQuery['tab'] === undefined) {
      const section = this.navItems.find(i => i.children?.includes(child));
      const first = section?.children?.find((c: NavLink) => c.route === child.route);
      return first === child;
    }
    return Object.entries(params).every(([k, v]) => this.currentQuery[k] === String(v));
  }

  /** Same-tab switch through the header's own selectModule (navigate + reload, as today). */
  onOpenApp(app: BcApp): void {
    if (app.id === this.currentAppId) return;
    this.selectModule(app.id);
  }

  /** Where focus goes back to when the drawer / overlay sidebar closes. */
  private navOpener: HTMLElement | null = null;

  /** Phone drawer / tablet overlay: focus moves into it, and back to the opener on close. */
  openMobileNav(): void {
    this.navOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.isMobileMenuOpen = true;
    // After the drawer renders: the first control (the app switcher) gets focus.
    setTimeout(() => document.querySelector<HTMLElement>('bc-app-shell .sidebar button, bc-app-shell .sidebar a')?.focus());
  }

  closeMobileNav(): void {
    if (!this.isMobileMenuOpen) return;
    this.isMobileMenuOpen = false;
    const opener = this.navOpener;
    this.navOpener = null;
    // Not when the opener has gone (the rail button that became the full sidebar's own).
    if (opener?.isConnected) setTimeout(() => opener.focus());
  }

  toggleMobileNav(event?: Event): void {
    event?.stopPropagation();
    this.isMobileMenuOpen ? this.closeMobileNav() : this.openMobileNav();
  }

  /** The header's own Escape handler still closes the search panel; this one closes the drawer. */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    // The app switcher (inside the drawer) closes itself first; one Escape, one layer.
    if ((event.target as Element | null)?.closest?.('bc-app-switcher [role="dialog"]')) return;
    if (this.paletteOpen) this.closePalette();
    else if (this.overlayNav) this.closeMobileNav();
  }

  focusMain(event: Event): void {
    event.preventDefault();
    document.getElementById('main')?.focus();
  }

  // ---- Top bar ----

  /** Ctrl K / ⌘K: opens "Search or do anything" (docs/multi-org/design/Search.dc.html). */
  @HostListener('document:keydown.control.k', ['$event'])
  @HostListener('document:keydown.meta.k', ['$event'])
  onSearchShortcut(event: Event): void {
    event.preventDefault();
    this.openPalette();
  }

  /** Ctrl+Shift+B (the old Feedback launcher's shortcut): the palette on its Feedback actions. */
  @HostListener('document:keydown.control.shift.b', ['$event'])
  onFeedbackShortcut(event: Event): void {
    event.preventDefault();
    this.openPalette('>feedback');
  }

  // ---- Search or do anything (Search.dc.html) ----

  /** The palette's own box: "pf:" scopes the search to one organisation, ">" lists actions only. */
  readonly paletteControl = new FormControl('', { nonNullable: true });
  paletteOpen = false;
  /** The highlighted row in {@link paletteRows} (the first one, as on the board). */
  paletteIndex = 0;
  /** Organisations ticked in the "Searching in" menu; null follows the top bar's filter. */
  private paletteScope: number[] | null = null;
  private paletteOpener: HTMLElement | null = null;
  private lastScopeKey = '';
  private readonly reportFlow = inject(TicketReportFlowService);
  readonly TicketType = TicketType;
  @ViewChild('presenceAnchor', { read: ElementRef }) presenceAnchor?: ElementRef<HTMLElement>;
  /** The avatar's menu: closed by the organisations submenu when it opens a company here. */
  @ViewChild('avatarMenuTrigger') avatarTrigger?: MatMenuTrigger;

  openPalette(query = ''): void {
    this.paletteOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.isMobileMenuOpen = false;
    this.paletteOpen = true;
    this.paletteControl.setValue(query); // valueChanges → onPaletteInput
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      const end = this.paletteControl.value.length;
      this.searchInput?.nativeElement.setSelectionRange(end, end);
    });
  }

  closePalette(): void {
    if (!this.paletteOpen) return;
    this.paletteOpen = false;
    this.paletteControl.setValue('');
    this.searchControl.setValue('');
    this.paletteScope = null;
    this.activeResultIndex = -1;
    const opener = this.paletteOpener;
    this.paletteOpener = null;
    if (opener?.isConnected) setTimeout(() => opener.focus());
  }

  /** The organisation named by a leading "xx:" (its initials), if any. */
  private get prefixOrg(): OrgIdentity | null {
    const m = /^\s*([\p{L}\p{N}]{1,4}):/u.exec(this.paletteControl.value);
    if (!m) return null;
    return this.orgIdentity.orgs().find(o => o.initials.toLowerCase() === m[1].toLowerCase()) ?? null;
  }

  get actionsOnly(): boolean { return this.paletteControl.value.trimStart().startsWith('>'); }

  /** What the palette searches for once the "xx:" or ">" prefix is taken off. */
  private get paletteText(): string {
    let q = this.paletteControl.value.trimStart();
    if (q.startsWith('>')) return q.slice(1).trim();
    if (this.prefixOrg) q = q.replace(/^[^:]*:/, '');
    return q.trim();
  }

  /** The organisations searched: an "xx:" prefix, else the menu's ticks, else the top bar's filter. */
  get paletteScopeIds(): number[] {
    const prefix = this.prefixOrg;
    if (prefix) return [prefix.id];
    if (this.paletteScope) return this.paletteScope;
    return this.orgLens.isAll() ? [] : [...this.orgLens.selectedIds()];
  }

  get paletteScopeOrgs(): OrgIdentity[] {
    const ids = this.paletteScopeIds;
    return ids.length ? this.orgIdentity.orgs().filter(o => ids.includes(o.id)) : this.orgIdentity.orgs();
  }

  get paletteScopeLabel(): string {
    const ids = this.paletteScopeIds;
    const total = this.orgIdentity.orgs().length;
    if (!ids.length || ids.length >= total) return 'All organizations';
    if (ids.length === 1) return this.orgIdentity.find(ids[0])?.name || '1 organization';
    return `${ids.length} of ${total} organizations`;
  }

  /** The first organisation's initials, for the "Try pf:" hint. */
  get prefixHint(): string {
    return (this.orgIdentity.orgs()[0]?.initials || 'pf').toLowerCase() + ':';
  }

  /** Keeps the header's search (its services, scope and results) in step with the palette box. */
  onPaletteInput(): void {
    this.paletteIndex = 0;
    const text = this.actionsOnly ? '' : this.paletteText;
    this.selectedSearchCompanyIds = [...this.paletteScopeIds];
    const scopeKey = this.selectedSearchCompanyIds.join(',');
    const scopeChanged = scopeKey !== this.lastScopeKey;
    this.lastScopeKey = scopeKey;
    if (text !== (this.searchControl.value || '')) this.searchControl.setValue(text);
    else if (scopeChanged) this.runSearch();
  }

  togglePaletteOrg(companyId: number): void {
    const current = this.paletteScopeIds.length ? this.paletteScopeIds : this.orgIdentity.orgs().map(o => o.id);
    const next = current.includes(companyId) ? current.filter(id => id !== companyId) : [...current, companyId];
    this.paletteScope = next.length && next.length < this.orgIdentity.orgs().length ? next : null;
    this.onPaletteInput();
  }

  paletteOrgOn(companyId: number): boolean {
    const ids = this.paletteScopeIds;
    return !ids.length || ids.includes(companyId);
  }

  allPaletteOrgs(): void {
    this.paletteScope = null;
    this.onPaletteInput();
  }

  /** Commands: New…, the create entries, the tools that left the top bar, and every rail entry. */
  private paletteActions(): PaletteAction[] {
    const org = this.currentCompanyName;
    const saves = org ? `Saves in ${org}` : 'Saves in this tab\'s organization';
    const actions: PaletteAction[] = [];
    if (this.createItems.length) {
      actions.push({ key: 'new', icon: 'add', title: 'New…', detail: 'Pick what to create. ' + saves, keywords: 'new create add',
        run: () => this.openPalette('>New '), keep: true, always: true });
    }
    this.createItems.forEach((item, i) => actions.push({
      key: 'create-' + i, icon: item.icon || 'add', title: item.name, detail: saves, keywords: 'new create add',
      run: () => this.onNavChildClick(item),
    }));
    actions.push(
      { key: 'find', icon: 'manage_search', title: 'Find on this page', detail: 'Jump to a section or control here',
        keywords: 'tools page finder', run: () => this.pageFinder.toggle(), always: true },
      { key: 'presence', icon: 'groups', title: 'Who\'s active', detail: 'Colleagues available, away or offline',
        keywords: 'tools team presence people', run: () => this.openPresence(), always: true },
      { key: 'contacts', icon: 'contacts', title: 'Contacts', detail: 'Opens the phonebook', keywords: 'tools phonebook',
        run: () => this.onContactsClick(new MouseEvent('click')), always: true },
      { key: 'outlook', icon: 'mail_outline', title: 'Messages (Outlook)', detail: 'Your mailbox beside the page',
        keywords: 'tools email mail', run: () => this.toggleOutlookPanel(), always: true },
      { key: 'bug', icon: 'bug_report', title: 'Report a bug', detail: 'Feedback · Ctrl Shift B', keywords: 'feedback ticket',
        run: () => this.startFeedback(TicketType.Bug), always: true },
      { key: 'feature', icon: 'lightbulb', title: 'Request a feature', detail: 'Feedback', keywords: 'feedback ticket idea',
        run: () => this.startFeedback(TicketType.Feature) },
    );
    this.workspaceItems.forEach(item => actions.push({
      key: 'go-' + item.key, icon: item.icon, title: 'Go to ' + item.label, detail: item.externalUrl ? 'Opens in a new tab' : '',
      keywords: 'go open page',
      run: () => item.externalUrl ? window.open(item.externalUrl, '_blank', 'noopener') : this.openItem(item),
    }));
    return actions;
  }

  /** Actions matching the box: a few defaults when it is empty, all matches after ">". */
  private matchingActions(): PaletteAction[] {
    const all = this.paletteActions();
    const text = this.paletteText.toLowerCase();
    if (!text) return this.actionsOnly ? all.filter(a => !a.key.startsWith('go-')) : all.filter(a => a.always);
    const words = text.split(/\s+/).filter(Boolean);
    const hits = all.filter(a => {
      const hay = (a.title + ' ' + a.detail + ' ' + a.keywords).toLowerCase();
      return words.every(w => hay.includes(w));
    });
    return this.actionsOnly ? hits : hits.slice(0, 4);
  }

  /** Rows in display order: Actions, then each module's results. Rebuilt per change detection pass. */
  get paletteGroups(): PaletteGroup[] {
    const actions = this.matchingActions();
    const groups: PaletteGroup[] = actions.length ? [{ label: 'Actions', rows: actions.map(a => ({ action: a })) }] : [];
    if (!this.actionsOnly && this.paletteText.length >= 2) {
      for (const g of this.searchResults) {
        if (g.results.length) groups.push({ label: g.category, rows: g.results.map(r => ({ result: r })) });
      }
    }
    let i = 0;
    groups.forEach(g => g.rows.forEach(r => r.index = i++));
    return groups;
  }

  /** The getter builds fresh rows each pass; these keep the same DOM rows across passes. */
  trackGroup(_: number, g: PaletteGroup): string { return g.label; }
  trackRow(_: number, r: PaletteRow): string {
    return r.action ? 'a:' + r.action.key : 'r:' + r.result!.type + ':' + r.result!.id + ':' + r.result!.companyId;
  }

  private get paletteFlat(): PaletteRow[] {
    return this.paletteGroups.flatMap(g => g.rows);
  }

  onPaletteKeydown(event: KeyboardEvent): void {
    const rows = this.paletteFlat;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!rows.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.paletteIndex = (this.paletteIndex + step + rows.length) % rows.length;
      setTimeout(() => document.getElementById('bc-palette-row-' + this.paletteIndex)?.scrollIntoView({ block: 'nearest' }));
      return;
    }
    if (event.key === 'Enter') {
      const row = rows[this.paletteIndex];
      if (row) { event.preventDefault(); this.runPaletteRow(row); }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closePalette();
    }
  }

  runPaletteRow(row: PaletteRow, event?: MouseEvent): void {
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) return;
    event?.preventDefault();
    if (row.action) {
      const action = row.action;
      if (!action.keep) this.closePalette();
      action.run();
      return;
    }
    const result = row.result!;
    if (result.restricted) { this.onSearchResultClick(result); return; }
    this.closePalette();
    this.onSearchResultClick(result);
  }

  /** The record's detail line: its module subtitle, where else it is, and the name-only case. */
  resultDetail(result: GlobalSearchResult): string {
    const parts = [result.subtitle];
    if (result.availableInCompanies?.length) parts.push('also in ' + result.availableInCompanies.join(', '));
    if (result.restricted) parts.push('you see the name only');
    return parts.filter(Boolean).join(' · ');
  }

  /** Feedback (report a bug / request a feature), started once the palette and menus are gone. */
  startFeedback(type: TicketType): void {
    setTimeout(() => void this.reportFlow.start({ type, route: this.nav.url, hideDuringCapture: [] }), 250);
  }

  /** Who's active: the team presence panel, anchored to the avatar. */
  openPresence(): void {
    setTimeout(() => this.presenceAnchor?.nativeElement.querySelector<HTMLElement>('button')?.click(), 50);
  }

  // ---- Stripe and rail organisations ----

  /** The 4px stripe across the column: one band per organisation shown (the top bar's filter). */
  readonly orgLensShown = this.orgLens.selectedOrgs;

  /** The rail's "Your organizations": shows only that one on My work (a filter, never a switch). */
  showOnlyOrg(org: OrgIdentity, event?: MouseEvent): void {
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) return;
    event?.preventDefault();
    this.orgLens.only(org.id);
    const myWork = this.workspaceItems.find(i => i.key === 'my-work');
    if (myWork) this.openItem(myWork);
  }

  isOnlyOrg(org: OrgIdentity): boolean {
    return !this.orgLens.isAll() && this.orgLens.selectedIds().length === 1 && this.orgLens.selectedIds()[0] === org.id;
  }

  get myWorkHref(): string {
    return this.workspaceItems.find(i => i.key === 'my-work')?.href || '/';
  }

  // ---- First sign-in (Welcome.dc.html) ----

  /** Welcome seen, favourites and start view: Identity, with localStorage as cache and fallback. */
  private readonly prefs = inject(WorkspacePreferencesService);
  private welcomeManual = false;
  private welcomeDismissed = false;
  /** Opens by itself only once the server has answered, so a device already past it never flashes it. */
  get welcomeOpen(): boolean {
    return this.welcomeManual || (!this.welcomeDismissed && this.prefs.settled() && !this.prefs.welcomeSeen());
  }
  private readonly favouriteSet = computed(() => new Set(this.prefs.favourites()));
  get favourites(): Set<number> { return this.favouriteSet(); }
  /** The Welcome panel's pick, saved on "Open my workspace". */
  startView: StartView = this.prefs.startView();
  readonly startViews: { id: StartView; label: string }[] = [
    { id: 'all', label: 'All organizations' },
    { id: 'fav', label: 'My favourites' },
    { id: 'last', label: 'Whatever I used last' },
  ];
  private appliedView: StartView | null = null;
  private appliedFinal = false;
  /**
   * Once the organisations are known (so the user is signed in): reads the preferences, applies the
   * chosen start view from the local cache at once, and again if the server's answer differs.
   */
  private readonly applyStartView = effect(() => {
    const orgs = this.orgIdentity.orgs();
    if (!orgs.length) return;
    const settled = this.prefs.settled();
    const chosen = this.prefs.startViewChosen();
    const view = this.prefs.startView();
    untracked(() => {
      this.prefs.ensureLoaded();
      if (!this.welcomeManual) this.startView = view;
      if (this.appliedFinal || !chosen) { if (settled) this.appliedFinal = true; return; }
      if (this.appliedView === null || (settled && view !== this.appliedView)) {
        this.appliedView = view;
        this.useStartView();
      }
      if (settled) this.appliedFinal = true;
    });
  });

  get showWelcome(): boolean {
    return this.welcomeOpen && this.loginDisplay && !this.isOnboarding && this.orgIdentity.isMultiOrg();
  }

  startViewHint(id: StartView): string {
    if (id === 'all') return 'Everything, grouped by org (recommended)';
    if (id === 'fav') return `${this.favourites.size} organization${this.favourites.size === 1 ? '' : 's'} you starred`;
    return 'Each page remembers your filter';
  }

  get masterCompanyName(): string {
    const id = (this.currentUser as any)?.managerCompanyId;
    return id ? (this.userCompanies?.find(c => c.id === id)?.name || '') : '';
  }

  isMasterCompany(org: OrgIdentity): boolean {
    return !!org.id && org.id === (this.currentUser as any)?.managerCompanyId;
  }

  toggleFavourite(org: OrgIdentity): void {
    const ids = this.prefs.favourites();
    this.prefs.update({ favouriteCompanyIds: ids.includes(org.id) ? ids.filter(id => id !== org.id) : [...ids, org.id] });
  }

  openWelcome(): void {
    this.startView = this.prefs.startView();
    this.welcomeManual = true;
  }

  finishWelcome(): void {
    this.prefs.update({ welcomeSeen: true, startView: this.startView });
    this.welcomeManual = false;
    this.welcomeDismissed = true;
    this.appliedView = this.startView;
    this.useStartView();
  }

  /** "Admin", "Viewer"… in one organisation; null on a backend that doesn't send it (column left empty). */
  roleIn(org: OrgIdentity): string | null {
    return this.orgIdentity.roleName(org.id);
  }

  /** "Corporate, Invoicing" from the user's module access in one organisation; '' when not sent. */
  modulesIn(org: OrgIdentity): string {
    const access = this.userCompanies?.find(c => c.id === org.id)?.modules;
    if (!access) return '';
    return Object.entries(access)
      .filter(([, level]) => !!level)
      .map(([id, level]) => {
        const name = this.moduleNames[+id];
        if (!name) return '';
        return +level === VIEWER_ACCESS ? `${name} (view only)` : name;
      })
      .filter(Boolean)
      .join(', ');
  }

  /** True when the backend sends roles or module access, so the Welcome table shows those columns. */
  get welcomeHasAccessColumns(): boolean {
    return this.orgIdentity.orgs().some(o => !!this.roleIn(o) || !!this.modulesIn(o));
  }

  private useStartView(): void {
    if (this.startView === 'all') { this.orgLens.showAll(); return; }
    if (this.startView !== 'fav') return;
    const ids = this.orgIdentity.orgs().map(o => o.id).filter(id => this.favourites.has(id));
    if (!ids.length) return;
    this.orgLens.only(ids[0]);
    ids.slice(1).forEach(id => this.orgLens.toggle(id));
  }

  // ---- Avatar menu ----

  get canCollapseRail(): boolean { return !this.layout.isPhone() && !this.layout.useRail(); }

  /**
   * The first crumb (the app's name, "Home" for Corporate) goes to the app's landing page: the
   * dashboard's Overview for Corporate, the same page the app switcher opens for the others.
   */
  goToAppHome(event?: MouseEvent): void {
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) return;
    event?.preventDefault();
    if (this.currentAppId == null) return;
    this.nav.navigate(this.appHomeCommands, { queryParams: this.appHomeParams });
  }

  get appHomeHref(): string {
    if (this.currentAppId == null) return '/';
    return this.nav.serializeUrl(this.nav.createUrlTree(this.appHomeCommands, { queryParams: this.appHomeParams }));
  }

  private get appHomeCommands(): any[] {
    return this.moduleLandingRoute(this.currentAppId!) ?? [''];
  }

  private get appHomeParams(): Record<string, any> {
    const landing = this.moduleLandingRoute(this.currentAppId!);
    return landing ? { module: this.currentAppId } : { module: this.currentAppId, tab: 'overview' };
  }

  /** Company Settings shortcuts in the organisation menu (same list as the dashboard's). */
  readonly companySettingsSections = COMPANY_SETTINGS_SECTIONS;
  private readonly corporateAccess = inject(PermissionsService);

  /**
   * Admins of this tab's organisation get the settings menu on the organisation crumb; everyone
   * else only sees the name. /settings/company/:id is behind pagePermissionGuard, so a non-admin
   * would only be bounced to '/'.
   */
  get canOpenCompanySettings(): boolean {
    return this.corporateAccess.isCorporateAdmin();
  }

  /** Opens this tab's organisation in Company Settings, at `step` when given, in a new tab. */
  openCurrentCompanySettings(step?: string): void {
    const companyId = this.currentCompanyIdFromStorage || this.companiesService.managedCompanyId;
    if (!companyId) return;
    this.companyForms.getInternalCompanyFormId(companyId).pipe(catchError(() => of(null))).subscribe(formId => {
      if (!formId) {
        this.notify.open('No internal client form found for this company.', 'Close', { duration: 3000 });
        return;
      }
      const urlTree = this.nav.createUrlTree(['/settings/company', companyId], {
        queryParams: { submittedFormId: formId, org: companyId, ...(step ? { step } : {}) },
      });
      window.open(this.nav.serializeUrl(urlTree), '_blank');
    });
  }
  private readonly companyForms = inject(CorporateFormsService);
  private readonly notify = inject(MatSnackBar);

  // ---- Page finder ----

  /**
   * The sidebar's places for the "What are you looking for?" card: this app's pages (sub-pages
   * included, collapsed or not), Create, the other apps and the account settings. Each one opens
   * through the same handler as its sidebar link.
   */
  private finderItems(): FinderItem[] {
    const workspace: FinderItem[] = this.workspaceItems.map(item => ({
      label: item.label, icon: item.icon, group: 'Go to', keywords: 'workspace', hint: item.externalUrl ? 'New tab' : undefined,
      run: () => item.externalUrl ? window.open(item.externalUrl, '_blank', 'noopener') : this.openItem(item),
    }));
    const pages =this.visibleNavItems.flatMap((item): FinderItem[] => item.children
      ? item.children.map(child => ({
          label: child.name, hint: item.name, keywords: item.name, group: 'Go to',
          run: () => this.onNavChildClick(child),
        }))
      : [{ label: item.name, icon: item.icon, group: 'Go to', run: () => this.onNavItemClick(item) }]);

    const create: FinderItem[] = this.createItems.map(item => ({
      label: item.name, group: 'Create', keywords: 'new add create', run: () => this.onNavChildClick(item),
    }));

    const apps: FinderItem[] = this.apps
      .filter(app => app.status === 'available' && app.id !== this.currentAppId)
      .map(app => ({
        label: app.label, hint: app.externalUrl ? 'New tab' : undefined, group: 'Apps',
        keywords: 'app module switch ' + app.group,
        run: () => app.externalUrl ? window.open(app.externalUrl, '_blank', 'noopener') : this.onOpenApp(app),
      }));

    const settings: FinderItem[] = [
      { label: 'Profile', url: '/settings/user' },
      { label: 'Notification settings', url: '/settings/notifications' },
      { label: 'Organisation settings', url: '/settings/organization' },
      { label: 'My drafts', url: '/drafts' },
    ].map(s => ({ label: s.label, group: 'Settings', keywords: 'account', run: () => void this.nav.navigateByUrl(s.url) }));

    return [...workspace, ...pages, ...create, ...apps, ...settings];
  }

  // ---- Internals ----

  private get permissionsChanged$() { return this.ngxPermissions.permissions$; }
  private readonly ngxPermissions = inject(NgxPermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Level-2 items with their own children (Records › Individuals › View / Create) become one link
   * to their "View" page. The Create entries move to the Create button, so the sidebar stays two
   * levels deep.
   */
  private foldChildren(children: any[] | null | undefined): NavLink[] | null {
    if (!children?.length) return null;
    return children.map(child => {
      if (!child.children?.length) return child;
      const view = child.children.find((c: NavLink) => !isCreateItem(c)) ?? child.children[0];
      return { ...view, name: child.name, icon: child.icon, children: null };
    });
  }

  /**
   * Resolves access itself rather than reading the header's moduleAccess map: that map is filled
   * asynchronously off the same permission changes, so reading it here could see the previous set.
   * Runs overlap on quick permission changes; only the newest one publishes (as in the header).
   */
  private async buildApps(modules: IModule[]): Promise<void> {
    const run = ++this.appsRun;
    const access = await Promise.all(modules.map(m => this.apps$.hasModuleAccess(m.id)));
    if (run !== this.appsRun) return;
    const origin = window.location.origin;
    this.apps = modules
      // Only apps the user can open, plus coming-soon ones (shown disabled). Unlike the old grid,
      // no greyed-out tiles for modules without access.
      .filter((m, i) => m.status === 'coming-soon' || access[i])
      .map(m => ({
        id: m.id,
        label: m.name,
        icon: m.icon,
        group: APP_GROUPS[m.id] ?? 'Other',
        status: m.status === 'coming-soon' ? 'soon' as const : 'available' as const,
        externalUrl: m.externalUrl || undefined,
        newTabHref: m.externalUrl || m.status === 'coming-soon'
          ? undefined
          : origin + this.nav.serializeUrl(this.nav.createUrlTree([''], { queryParams: this.moduleTabParams(m.id) })),
      }))
      .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
    this.appAccess = Object.fromEntries(modules.map((m, i) => [m.id, access[i] && m.status !== 'coming-soon']));
    this.accessKnown = true;
    this.rebuildWorkspace();
    this.updateBreadcrumb();
  }

  /**
   * Query params for opening a module in a new tab. Organizations are per tab (OrgScopeStore), and
   * the switcher opens with noopener, so without ?org= the new tab fell back to the first
   * organization opened in the browser instead of the one this tab is on.
   */
  private moduleTabParams(moduleId: number): Record<string, number> {
    const org = this.companiesService.managedCompanyId;
    return org ? { module: moduleId, org } : { module: moduleId };
  }

  private readUrl(url: string): void {
    const tree = this.nav.parseUrl(url);
    const previousPath = this.currentPath;
    this.currentPath = '/' + (tree.root.children['primary']?.segments.map(s => s.path).join('/') ?? '');
    this.currentQuery = tree.queryParams as Record<string, string>;
    if (this.currentPath !== previousPath) this.expandedOnRecord = false;
    this.onRecordPage = /^\/(edit-form|settings\/company)\//.test(this.currentPath);
    this.isChatPage = this.openedForNotes && this.currentPath === '/tagged-notes';
    this.expandActive();
    this.refreshActive();
    this.updateBreadcrumb();
  }

  /** Only the active section is expanded (SPEC.md): navigating collapses the others. */
  private expandActive(): void {
    // A section is open when the page on screen is one of its children (exact match, the same check
    // that highlights the child) or its own route. Sections the user opened by hand stay open until
    // they close them or move to another section.
    const active = this.navItems.filter(i => i.children && this.isSectionActive(i)).map(i => i.name);
    if (!active.length) return;
    this.expanded = new Set(active);
  }

  private isSectionActive(item: NavLink): boolean {
    return (item.children || []).some((c: NavLink) => this.isChildActive(c)) || this.isNavItemActive(item);
  }

  private updateBreadcrumb(): void {
    const app = this.apps.find(a => a.id === this.currentAppId)?.label;
    const section = this.navItems.find(i => i.children ? this.isSectionActive(i) : this.isNavItemActive(i));
    const page = section?.children?.find(c => this.isChildActive(c));
    this.breadcrumb = [app, section?.name, page?.name].filter((s): s is string => !!s);
    this.refreshTabs();
  }

  /**
   * Corporate gets the mockup's tabs: Home · Clients · Planner · Notes (+ More). Its client records
   * count as Clients. Other apps get Home and Notes; their own pages are in the drawer (More).
   */
  private refreshTabs(): void {
    const path = this.currentPath;
    const tabs: BcTab[] = [{
      label: 'Home', icon: 'home',
      commands: this.currentAppId == null ? [''] : this.appHomeCommands,
      queryParams: this.currentAppId == null ? null : this.appHomeParams,
      active: this.currentAppId != null && path === '/' + (this.appHomeCommands[0] || '').replace(/^\/+/, ''),
    }];
    if (this.currentAppId === 1) {
      tabs.push(
        { label: 'Clients', icon: 'groups', commands: ['/forms-table'], queryParams: { category: 'clients' },
          active: /^\/(forms-table|edit-form\/|create-form\/)/.test(path) },
        { label: 'Planner', icon: 'checklist', commands: ['/task-planner'], active: path.startsWith('/task-planner') },
      );
    }
    if (this.canSeeNotesNav) {
      tabs.push({ label: 'Notes', icon: 'sticky_note_2', commands: ['/tagged-notes'], active: path.startsWith('/tagged-notes'),
        badge: this.notesUnread || undefined, badgeLabel: 'unread' });
    }
    this.phoneTabs = tabs;
  }
}

/** One command in "Search or do anything". `always`: listed while the box is empty; `keep`: stays open. */
interface PaletteAction {
  key: string; icon: string; title: string; detail: string; keywords: string;
  run: () => void; always?: boolean; keep?: boolean;
}
interface PaletteRow { action?: PaletteAction; result?: GlobalSearchResult; index?: number; }
interface PaletteGroup { label: string; rows: PaletteRow[]; }
type StartView = 'all' | 'fav' | 'last';

/** UserCompany.modules value for a module Viewer (Corporate 17): read-only access. */
const VIEWER_ACCESS = 17;

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}

function isCreateRoute(route: string | undefined): boolean {
  return !!route && route.startsWith('create-form/');
}

/** A create entry: the create form, or a list page told to start its create (?create=, e.g. Shelf Names). */
function isCreateItem(item: any): boolean {
  return isCreateRoute(item?.route) || !!item?.queryParams?.create;
}

function collectCreateItems(items: any[]): NavLink[] {
  const out: NavLink[] = [];
  const walk = (nodes: any[] | null | undefined, parent?: string) => (nodes || []).forEach(n => {
    if (isCreateItem(n)) {
      // "Create" alone under Individuals reads as "New individual"; named ones keep their name.
      const name = n.name === 'Create' && parent ? `New ${singular(parent)}` : n.name.replace(/^Create /, 'New ');
      out.push({ ...n, name: sentence(name) });
    }
    walk(n.children, n.name);
  });
  walk(items);
  return out;
}

function singular(name: string): string {
  return name.replace(/ies$/, 'y').replace(/s$/, '').toLowerCase();
}

function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
